import React, { useEffect, useState } from "react";

import { StoreData, StoreDateType } from "../../../@types/productInfo";

import storeServiceInstance from "../../../APIs/store";
import StoreTable from "./StoreTable/StoreTable";
import { useNotification } from "../../Toast/Notification";
import HeaderTable from "../HeaderTable/HeaderTable";
import StoreModal from "../../Modal/StoreModal/StoreModal";
import { Modal } from "antd";
import useSearchItem from "../../../hooks/use-search-item";
import { usePagination } from "../../../hooks/use-pagination";

const Store: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState("all");
  const { notify } = useNotification();
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 6;

  const [storeName, setStoreName] = useState<string>("");
  const [storeId, setStoreId] = useState<string>("");
  const [meta_title, setMetaTitle] = useState<string>("");
  const [meta_description, setMetaDescription] = useState<string>("");
  const [storeNo, setStoreNo] = useState<string>("");
  const [streetAddress, setStreetAddress] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [zip, setZip] = useState<string>("");
  const [phoneNo, setPhoneNo] = useState<number>(0);
  const [status, setStatus] = useState<string>("");
  const [lat, setLat] = useState<number>(0);
  const [lng, setLng] = useState<string>("");
  const [addressType, setAddressType] = useState<string>("");
  const [locationType, setLocationType] = useState<string>("");
  const [sellmachine, setSellMachine] = useState<boolean>(false);
  const [exchange, setExchange] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [selectedStore, setSelectedStore] = useState<StoreDateType | null>(
    null
  );

  const [stores, setStores] = useState<StoreDateType[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredStore, setFilteredStore] = useState<StoreDateType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleButtonClickk = () => {
    setIsModalVisible(true);
    setIsUpdating(false);
  };

  const handlePageChange = (pageNumber: any) => {
    setCurrentPage(pageNumber);
  };

  const filterProductsFromSearch = useSearchItem({
    items: stores,
    searchTerm,
    selectedRole,
    searchFields: [
      "storeName",
      "storeId",
      "storeNo",
      "streetAddress",
      "city",
      "state",
      "zip",
      "phoneNo",
      "status",
      "lat",
      "lng",
      "addressType",
      "locationType",
    ],
  });

  useEffect(() => {
    if (searchTerm.length > 0) {
    } else {
      setFilteredStore(stores);
    }
  }, [stores, filterProductsFromSearch, searchTerm]);

  const totalPages = Math.ceil(filteredStore.length / productsPerPage);
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredStore.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const pageNumbers = usePagination(totalPages);

  const handleSearchTermChange = (e: any) => {
    setSearchTerm(e.target.value);
  };

  const handleRoleChange = (event: any) => {
    setSelectedRole(event.target.value);
  };

  // Function to handle form submission

  const handleFormSubmit = async (data: StoreData) => {
    const userData: StoreDateType = {
      storeName: storeName,
      storeId: storeId,
      storeNo: storeNo,
      streetAddress: streetAddress,
      city: city,
      state: state,
      zip: zip,
      phoneNo: phoneNo,
      status: status,
      lat: lat,
      lng: lng,
      addressType: addressType,
      locationType: locationType,
      sell_machine: sellmachine,
      cylinder_exchange: exchange,
      meta_title: meta_title,
      meta_description: meta_description,
    };

    try {
      setIsLoading(true);
      const { data, error } = await storeServiceInstance.createStore(userData);

      if (data) {
        fetchStoreData();

        setIsModalVisible(false);

        notify("Store created successfully", "success");
        setTimeout(() => {}, 3000);
      } else {
        console.error("Error creating store:", error);
      }
    } catch (error) {
      console.error("Error creating store:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditStore = (store: any) => {
    setSelectedStore(store);

    setStoreName(store.storeName || "");
    setStoreId(store.storeId || "");
    setStoreNo(store.storeNo || "");
    setStreetAddress(store.streetAddress || "");
    setCity(store.city || "");
    setState(store.state || "");
    setZip(store.zip || "");
    setPhoneNo(store.phoneNo || 0);
    setStatus(store.status || "");
    setLat(store.lat || 0);
    setLng(store.lng || "");
    setAddressType(store.addressType || "");
    setLocationType(store.locationType || "");
    setSellMachine(store.sell_machine);
    setExchange(store.cylinder_exchange);
    setMetaDescription(store.meta_description);
    setMetaTitle(store.meta_title);

    setIsUpdating(true);
    setIsModalVisible(true);
  };

  const handleSaveEdit = async (storeData: StoreData) => {
    try {
      if (selectedStore && selectedStore.id) {
        const updatedAccData = {
          storeName: storeName,
          storeId: storeId,
          storeNo: storeNo,
          streetAddress: streetAddress,
          city: city,
          state: state,
          zip: zip,
          phoneNo: phoneNo,
          status: status,
          lat: lat,
          lng: lng,
          addressType: addressType,
          locationType: locationType,
          sell_machine: sellmachine,
          cylinder_exchange: exchange,
          meta_title: meta_title,
          meta_description: meta_description,
        };

        const { data } = await storeServiceInstance.editStore(
          selectedStore.id,
          updatedAccData
        );

        if (data) {
          notify("Store updated successfully", "success");
          fetchStoreData();

          setSelectedStore(null);
          setIsModalVisible(false);
          setTimeout(() => {}, 3000);
        } else {
          console.error("Error updating store:");
          notify("Error updating store", "error");
        }
      } else {
        console.error("Selected store or store id is undefined");
      }
    } catch (error) {
      console.error("Error updating store:", error);
    } finally {
    }
  };

  const fetchStoreData = async () => {
    try {
      const { data } = await storeServiceInstance.getAllStores();

      if (data) {
        const sortedData = data
          .slice()
          .sort((a, b) => a.storeName.localeCompare(b.storeName));

        setStores(sortedData);
        setFilteredStore(data);
      } else {
        // console.error("Error fetching store:", error);
      }
    } catch (error) {
      console.error("Error fetching store:", error);
    }
  };

  useEffect(() => {
    fetchStoreData();
  }, []);

  const handleDeleteStore = async (storeId: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this item?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        if (storeId) {
          try {
            const { data, error } = await storeServiceInstance.deleteStore(
              storeId
            );

            if (data) {
              setStores((prev) => prev.filter((store) => store.id !== storeId));

              notify("store deleted successfully", "success");

              fetchStoreData();

              setSelectedStore(null);
              setIsModalVisible(false);
              setTimeout(() => {}, 3000);
            } else {
              console.error("Error deleting store:", error);
              notify("Error deleting store", "error");
            }
          } catch (error) {
            console.error("Error deleting store:", error);
          } finally {
          }
        }
      },
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setStoreName("");
    setStoreId("");
    setStoreNo("");
    setStreetAddress("");
    setCity("");
    setState("");
    setZip("");
    setPhoneNo(0);
    setStatus("");
    setLat(0);
    setLng("");
    setAddressType("");
    setLocationType("");
    setMetaDescription("");
    setMetaTitle("");

    setIsUpdating(false);
  };

  return (
    <div className="p-4 rounded-md w-5/4 mx-auto">
      <p
        className="mb-[15px] box-border font-semibold 
            text-[rgb(0,34,56)] block text-[35px] text-left h-auto
            leading-[38.4px]">
        Stores
      </p>

      <div className="bg-white p-4 rounded-md w-5/4 mx-auto">
        <HeaderTable
          subtitle="Manage Stores"
          buttonText="Add New Store"
          inputPlaceholder="Search Store"
          selectPlaceholder="Filter By Product Type"
          loading={false}
          searchTerm={searchTerm}
          selectedValue={selectedRole}
          onButtonClick={handleButtonClickk}
          onSearchChange={handleSearchTermChange}
          onSelectChange={handleRoleChange}
          selectOptions={[
            { value: "flavour", label: "Flavours" },
            { value: "cylinders", label: "Cylinder" },
          ]}
        />

        <StoreTable
          products={filteredStore}
          loading={isLoading}
          onEditOrder={handleEditStore}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          pageNumbers={pageNumbers}
          currentProducts={currentProducts}
          handleDeleteAcc={handleDeleteStore}
        />

        <StoreModal
          visible={isModalVisible}
          onClose={handleModalCancel}
          isProcessing={isLoading}
          isModalVisible={isModalVisible}
          storeName={storeName}
          storeId={storeId}
          storeNo={storeNo}
          streetAddress={streetAddress}
          city={city}
          state={state}
          zip={zip}
          phoneNo={phoneNo}
          status={status}
          lat={lat}
          lng={lng}
          addressType={addressType}
          locationType={locationType}
          sellMachine={sellmachine}
          exchange={exchange}
          metaTitle={meta_title}
          metaDescription={meta_description}
          setStoreName={setStoreName}
          setStoreId={setStoreId}
          setStoreNo={setStoreNo}
          setStreetAddress={setStreetAddress}
          setCity={setCity}
          setState={setState}
          setZip={setZip}
          setPhoneNo={setPhoneNo}
          setStatus={setStatus}
          setLat={setLat}
          setLng={setLng}
          setAddressType={setAddressType}
          setLocationType={setLocationType}
          setSellMachine={setSellMachine}
          setExchange={setExchange}
          setMetaTitle={setMetaTitle}
          setMetaDescription={setMetaDescription}
          isUpdating={isUpdating}
          sellmachine={false}
          meta_title={meta_title}
          meta_description={meta_description}
          handleFormSubmit={handleFormSubmit}
          handleSaveEdit={handleSaveEdit}
          handleModalCancel={handleModalCancel}
        />
      </div>
    </div>
  );
};

export default Store;
